import {assertTokenValidForMutation, handleResponse} from "@/services/service-helpers.ts";
import log from '@/lib/logger';

const NBH_PASSWORD_POLICY_URL = `${import.meta.env.VITE_NBH_URL_ROOT}/api/password-policy`
const NBH_ADMIN_PASSWORD_POLICY_URL = `${import.meta.env.VITE_NBH_URL_ROOT}/api/admin/password-policy`
const NBH_ADMIN_PASSWORD_BLOCKLIST_URL = `${import.meta.env.VITE_NBH_URL_ROOT}/api/admin/password-blocklist`

// Form-facing view of the password policy in effect for the caller's organization, returned by
// GET /api/password-policy. Mirrors EffectivePasswordPolicyResponse on the backend. The strength
// constraints (minDistinctChars, minStrengthScore) are surfaced here so the user-facing forms can
// render the effective minimum strength next to the indicator. Both default to 0 on the server
// when the admin has not opted in; the server stays authoritative on submit.
export interface EffectivePasswordPolicy {
    minLength: number
    requireDigit: boolean
    requireSpecialChar: boolean
    minDistinctChars: number
    minStrengthScore: number
}

// Mirrors the backend default (NbhPasswordPolicyConfiguration.DEFAULT_MIN_LENGTH). Used only as a
// placeholder until the real policy is fetched; the backend stays the source of truth and
// re-validates every password on submit.
export const DEFAULT_PASSWORD_MIN_LENGTH = 12

export type EffectivePasswordPolicySource = "INSTALLATION_DEFAULT" | "ORGANIZATION_OVERRIDE"

// Admin-facing view of the effective policy returned by GET/PUT/DELETE /api/admin/password-policy.
// Carries the source so the settings page can show "using installation defaults" vs "organization
// override", and the per-organization toggle so the form can render read-only when overrides are
// disabled installation-wide. Mirrors AdminPasswordPolicyResponse on the backend.
export interface AdminPasswordPolicyResponse {
    minLength: number
    requireDigit: boolean
    requireSpecialChar: boolean
    blockUsername: boolean
    blockRealName: boolean
    blockOrganizationName: boolean
    bundledBlocklistEnabled: boolean
    hibpEnabled: boolean
    minDistinctChars: number
    minStrengthScore: number
    source: EffectivePasswordPolicySource
    perOrganizationEnabled: boolean
}

// Whole-policy payload for PUT /api/admin/password-policy. No field-level inheritance per ADR-0005.
// bundledBlocklistEnabled is intentionally absent: it is an installation-level concern.
export interface PasswordPolicyUpdateRequest {
    minLength: number
    requireDigit: boolean
    requireSpecialChar: boolean
    blockUsername: boolean
    blockRealName: boolean
    blockOrganizationName: boolean
    hibpEnabled: boolean
    minDistinctChars: number
    minStrengthScore: number
}

export async function getEffectivePasswordPolicy(token: string) {
    const response = await fetch(NBH_PASSWORD_POLICY_URL, {
        method: 'GET',
        headers: new Headers({
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        }),
    })

    return handleResponse<EffectivePasswordPolicy>(response)
}

export async function getAdminPasswordPolicy(
    token: string | null,
): Promise<AdminPasswordPolicyResponse | void> {
    if (!token) {
        log.error("No token provided for admin password policy retrieval.")
        return Promise.reject("No token provided")
    }

    const response = await fetch(NBH_ADMIN_PASSWORD_POLICY_URL, {
        method: 'GET',
        headers: new Headers({
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        }),
    })

    return handleResponse<AdminPasswordPolicyResponse>(response)
}

export async function updateAdminPasswordPolicy(
    token: string | null,
    request: PasswordPolicyUpdateRequest,
): Promise<AdminPasswordPolicyResponse | void> {
    if (!token) {
        log.error("No token provided for admin password policy update.")
        return Promise.reject("No token provided")
    }
    assertTokenValidForMutation()

    const response = await fetch(NBH_ADMIN_PASSWORD_POLICY_URL, {
        method: 'PUT',
        headers: new Headers({
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        }),
        body: JSON.stringify(request),
    })

    return handleResponse<AdminPasswordPolicyResponse>(response)
}

export async function resetAdminPasswordPolicy(
    token: string | null,
): Promise<AdminPasswordPolicyResponse | void> {
    if (!token) {
        log.error("No token provided for admin password policy reset.")
        return Promise.reject("No token provided")
    }
    assertTokenValidForMutation()

    const response = await fetch(NBH_ADMIN_PASSWORD_POLICY_URL, {
        method: 'DELETE',
        headers: new Headers({
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        }),
    })

    return handleResponse<AdminPasswordPolicyResponse>(response)
}

// One entry of the organization's additive blocklist, returned by GET /api/admin/password-blocklist
// and POST. Mirrors PasswordBlocklistEntryResponse on the backend. addedAt is an ISO-UTC timestamp
// (@CreationTimestamp); the bundled installation-wide list is never part of this collection.
export interface PasswordBlocklistEntry {
    id: string
    word: string
    addedAt: string
}

export async function listPasswordBlocklist(
    token: string | null,
): Promise<PasswordBlocklistEntry[] | void> {
    if (!token) {
        log.error("No token provided for password blocklist retrieval.")
        return Promise.reject("No token provided")
    }

    const response = await fetch(NBH_ADMIN_PASSWORD_BLOCKLIST_URL, {
        method: 'GET',
        headers: new Headers({
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        }),
    })

    return handleResponse<PasswordBlocklistEntry[]>(response)
}

export async function addPasswordBlocklistEntry(
    token: string | null,
    word: string,
): Promise<PasswordBlocklistEntry | void> {
    if (!token) {
        log.error("No token provided for password blocklist addition.")
        return Promise.reject("No token provided")
    }
    assertTokenValidForMutation()

    const response = await fetch(NBH_ADMIN_PASSWORD_BLOCKLIST_URL, {
        method: 'POST',
        headers: new Headers({
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        }),
        body: JSON.stringify({word}),
    })

    return handleResponse<PasswordBlocklistEntry>(response)
}

export async function deletePasswordBlocklistEntry(
    token: string | null,
    id: string,
): Promise<void> {
    if (!token) {
        log.error("No token provided for password blocklist deletion.")
        return Promise.reject("No token provided")
    }
    assertTokenValidForMutation()

    const response = await fetch(`${NBH_ADMIN_PASSWORD_BLOCKLIST_URL}/${id}`, {
        method: 'DELETE',
        headers: new Headers({
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        }),
    })

    return handleResponse<void>(response)
}
